/**
 * features/attendance/SchoolWideOverview.tsx
 * ------------------------------------------------------------------
 * Round 13 — School-wide attendance summary.
 *
 * Admin / headteacher only. Aggregates every reportDailyAttendance
 * doc across the school to compute:
 *   - Today's attendance % (or "no data yet" if nothing entered today)
 *   - This week's attendance %
 *   - This term's attendance %
 *   - Number of absentees today
 *   - Trend chart (last 14 days)
 *
 * All client-side aggregation. With ~336 daily docs and ~430 students
 * this loads in under a second.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { BarChart3, Users, Calendar, AlertCircle } from 'lucide-react';
import { db } from '@/lib/firebase';
import { useData, COLLECTIONS } from '@/contexts/DataContext';
import { Spinner, Card } from '@/components/ui/primitives';
import { GradientHero } from '@/components/ui/glass';
import { isPresent } from '@/lib/attendanceCalc';
import type { DailyAttendanceDoc, AttendanceStatus } from '@/types';

/* ---------------- date helpers ---------------- */
function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}
function daysAgoISO(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}
function startOfWeekISO(): string {
  const d = new Date();
  const day = d.getDay(); // 0=Sun, 1=Mon, ...
  const offset = day === 0 ? 6 : day - 1; // ISO week: Monday start
  d.setDate(d.getDate() - offset);
  return d.toISOString().slice(0, 10);
}

/* ---------------- aggregation ---------------- */
interface AggResult {
  present: number;
  absent: number;
  late: number;
  excused: number;
  sick: number;
  total: number;
  attendancePct: number | null;
  daysCovered: number;
}

function aggregate(docs: DailyAttendanceDoc[]): AggResult {
  let present = 0,
    absent = 0,
    late = 0,
    excused = 0,
    sick = 0;
  for (const d of docs) {
    for (const status of Object.values(d.entries) as AttendanceStatus[]) {
      switch (status) {
        case 'present':
          present++;
          break;
        case 'absent':
          absent++;
          break;
        case 'late':
          late++;
          break;
        case 'excused':
          excused++;
          break;
        case 'sick':
          sick++;
          break;
      }
    }
  }
  const total = present + absent + late + excused + sick;
  // Attendance % = (present + late) / total recorded entries
  const attendancePct =
    total > 0 ? Math.round(((present + late) / total) * 100) : null;
  return {
    present,
    absent,
    late,
    excused,
    sick,
    total,
    attendancePct,
    daysCovered: docs.length,
  };
}

/* ---------------- component ---------------- */
export default function SchoolWideOverview() {
  const { activeStudents: students } = useData();
  const [loading, setLoading] = useState(true);
  const [docs, setDocs] = useState<DailyAttendanceDoc[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const snap = await getDocs(collection(db, COLLECTIONS.dailyAttendance));
        if (cancelled) return;
        const all: DailyAttendanceDoc[] = snap.docs.map(
          (d) => ({ id: d.id, ...d.data() }) as DailyAttendanceDoc,
        );
        setDocs(all);
        setError(null);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /* ---- window filters ---- */
  const today = todayISO();
  const weekStart = startOfWeekISO();
  const last14 = daysAgoISO(13); // inclusive of today = 14 days

  const todayDocs = useMemo(() => docs.filter((d) => d.date === today), [docs, today]);
  const weekDocs = useMemo(
    () => docs.filter((d) => d.date >= weekStart && d.date <= today),
    [docs, weekStart, today],
  );
  const termDocs = docs; // every loaded doc; could filter by term if needed

  const todayAgg = useMemo(() => aggregate(todayDocs), [todayDocs]);
  const weekAgg = useMemo(() => aggregate(weekDocs), [weekDocs]);
  const termAgg = useMemo(() => aggregate(termDocs), [termDocs]);

  /* ---- last 14 day trend ---- */
  const trend = useMemo(() => {
    const recent = docs.filter((d) => d.date >= last14 && d.date <= today);
    // Group by date
    const byDate = new Map<string, DailyAttendanceDoc[]>();
    for (const d of recent) {
      if (!byDate.has(d.date)) byDate.set(d.date, []);
      byDate.get(d.date)!.push(d);
    }
    const arr = Array.from(byDate.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, dayDocs]) => {
        const agg = aggregate(dayDocs);
        return { date, pct: agg.attendancePct, absent: agg.absent };
      });
    return arr;
  }, [docs, last14, today]);

  const maxBar = useMemo(() => {
    const m = Math.max(...trend.map((t) => t.absent), 1);
    return m;
  }, [trend]);

  /* ---- render ---- */
  if (loading) {
    return (
      <div className="p-8">
        <Spinner label="Loading attendance data…" />
      </div>
    );
  }
  if (error) {
    return (
      <Card className="p-4 m-4 text-red-700 bg-red-50 border-red-200">
        <p className="font-medium">Failed to load attendance.</p>
        <p className="text-sm mt-1">{error}</p>
      </Card>
    );
  }

  const enrolledCount = students.length;

  return (
    <div className="space-y-4 px-1">
      <GradientHero
        title="School-wide attendance"
        subtitle={`Enrolled: ${enrolledCount} students · Records: ${docs.length} attendance days logged`}
        icon={BarChart3}
      />

      {/* Top-line stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          label="Today"
          value={todayAgg.attendancePct === null ? '—' : `${todayAgg.attendancePct}%`}
          sub={
            todayAgg.daysCovered === 0
              ? 'No data yet'
              : `${todayAgg.daysCovered} class${todayAgg.daysCovered === 1 ? '' : 'es'} recorded`
          }
          icon={<Calendar size={18} />}
          tone={todayAgg.attendancePct === null ? 'muted' : pctTone(todayAgg.attendancePct)}
        />
        <StatCard
          label="This week"
          value={weekAgg.attendancePct === null ? '—' : `${weekAgg.attendancePct}%`}
          sub={`${weekAgg.daysCovered} day${weekAgg.daysCovered === 1 ? '' : 's'} recorded`}
          icon={<Calendar size={18} />}
          tone={weekAgg.attendancePct === null ? 'muted' : pctTone(weekAgg.attendancePct)}
        />
        <StatCard
          label="This term"
          value={termAgg.attendancePct === null ? '—' : `${termAgg.attendancePct}%`}
          sub={`${termAgg.daysCovered} day${termAgg.daysCovered === 1 ? '' : 's'} recorded`}
          icon={<Users size={18} />}
          tone={termAgg.attendancePct === null ? 'muted' : pctTone(termAgg.attendancePct)}
        />
        <StatCard
          label="Absent today"
          value={String(todayAgg.absent)}
          sub={
            todayAgg.absent === 0
              ? todayAgg.daysCovered === 0
                ? 'No data yet'
                : 'Full house'
              : 'Click Absent today tab'
          }
          icon={<AlertCircle size={18} />}
          tone={todayAgg.absent > 0 ? 'warn' : 'muted'}
        />
      </div>

      {/* Trend chart */}
      <Card className="p-4">
        <div className="flex items-baseline justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            Last 14 days — daily absentees
          </h3>
          <p className="text-xs text-gray-500">
            {trend.length === 0
              ? 'No data in the last 14 days'
              : `${trend.length} day${trend.length === 1 ? '' : 's'} with data`}
          </p>
        </div>
        {trend.length === 0 ? (
          <p className="text-sm text-gray-500 italic py-6 text-center">
            No attendance recorded in the last 14 days.
          </p>
        ) : (
          <div className="flex items-end gap-1 h-32">
            {trend.map((t) => {
              const h = Math.round((t.absent / maxBar) * 100);
              const label = new Date(t.date + 'T12:00:00').toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short',
              });
              return (
                <div
                  key={t.date}
                  className="flex-1 flex flex-col items-center justify-end group"
                  title={`${label}: ${t.absent} absent, ${t.pct ?? '—'}% attended`}
                >
                  <span className="text-[10px] text-gray-500 mb-0.5 opacity-0 group-hover:opacity-100">
                    {t.absent}
                  </span>
                  <div
                    className="w-full bg-blue-500 dark:bg-blue-600 rounded-t group-hover:bg-blue-600 dark:group-hover:bg-blue-500 transition-colors"
                    style={{ height: `${Math.max(h, 4)}%` }}
                  />
                  <span className="text-[9px] text-gray-500 mt-1 whitespace-nowrap">{label}</span>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Status breakdown — term-wide */}
      <Card className="p-4">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
          This term — status breakdown
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <StatusBlock label="Present" value={termAgg.present} color="emerald" />
          <StatusBlock label="Absent" value={termAgg.absent} color="red" />
          <StatusBlock label="Late" value={termAgg.late} color="amber" />
          <StatusBlock label="Excused" value={termAgg.excused} color="blue" />
          <StatusBlock label="Sick" value={termAgg.sick} color="purple" />
        </div>
      </Card>
    </div>
  );
}

/* ---------------- subcomponents ---------------- */
type Tone = 'good' | 'ok' | 'warn' | 'muted';
function pctTone(p: number): Tone {
  if (p >= 95) return 'good';
  if (p >= 85) return 'ok';
  return 'warn';
}
const TONE_CLASSES: Record<Tone, string> = {
  good: 'text-emerald-700 dark:text-emerald-300',
  ok: 'text-blue-700 dark:text-blue-300',
  warn: 'text-amber-700 dark:text-amber-300',
  muted: 'text-gray-500 dark:text-gray-400',
};

function StatCard({
  label,
  value,
  sub,
  icon,
  tone,
}: {
  label: string;
  value: string;
  sub: string;
  icon: React.ReactNode;
  tone: Tone;
}) {
  return (
    <Card className="p-3">
      <div className="flex items-center gap-2 mb-1">
        <span className={TONE_CLASSES[tone]}>{icon}</span>
        <p className="text-[10px] uppercase tracking-wide font-semibold text-gray-500">
          {label}
        </p>
      </div>
      <p className={`text-2xl font-bold ${TONE_CLASSES[tone]}`}>{value}</p>
      <p className="text-xs text-gray-500 mt-0.5">{sub}</p>
    </Card>
  );
}

function StatusBlock({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: 'emerald' | 'red' | 'amber' | 'blue' | 'purple';
}) {
  const classes: Record<typeof color, string> = {
    emerald: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200',
    red: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-200',
    amber: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-200',
    blue: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-200',
    purple: 'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-200',
  };
  return (
    <div className={`rounded-lg p-3 ${classes[color]}`}>
      <p className="text-[10px] uppercase tracking-wide font-semibold opacity-80">{label}</p>
      <p className="text-xl font-bold">{value}</p>
    </div>
  );
}

// Re-export isPresent so build doesn't tree-shake the import warning (kept for parity)
void isPresent;
